/**
 * Transport Tool + Spawn Mode selects and stage chip sync.
 * Read-only view of ExhibitApp — does not touch ControlPanel state.
 */

import type { ExhibitApp } from "../../app/ExhibitApp";
import { POINTER_TOOLS } from "../../app/pointerTool";
import { getSpawnMode, SPAWN_MODES } from "../../app/spawnModes";

export function bindToolChrome(app: ExhibitApp): () => void {
  const toolSel = document.getElementById(
    "pointer-tool",
  ) as HTMLSelectElement | null;
  const modeSel = document.getElementById(
    "spawn-mode",
  ) as HTMLSelectElement | null;
  const modeWrap = document.getElementById(
    "spawn-mode-wrap",
  ) as HTMLElement | null;
  const toolStatus = document.getElementById("tool-status");

  // Single source of truth: POINTER_TOOLS / SPAWN_MODES (not static HTML options)
  if (toolSel) {
    toolSel.innerHTML = "";
    for (const t of POINTER_TOOLS) {
      const opt = document.createElement("option");
      opt.value = t.id;
      opt.textContent = t.label;
      opt.title = `${t.summary}\n\n${t.detail}`;
      toolSel.appendChild(opt);
    }
  }
  if (modeSel) {
    modeSel.innerHTML = "";
    for (const m of SPAWN_MODES) {
      const opt = document.createElement("option");
      opt.value = m.id;
      opt.textContent = m.label;
      opt.title = `${m.summary}\n\n${m.detail}`;
      modeSel.appendChild(opt);
    }
  }

  const sync = (): void => {
    if (toolSel && toolSel.value !== app.pointerTool) {
      toolSel.value = app.pointerTool;
    }
    const def = POINTER_TOOLS.find((t) => t.id === app.pointerTool);
    if (toolSel && def) {
      toolSel.title = `${def.summary}\n\n${def.detail}`;
    }

    const spawnOn = app.pointerTool === "spawn";
    if (modeWrap) modeWrap.hidden = !spawnOn;
    if (modeSel) {
      if (modeSel.value !== app.spawnModeId) modeSel.value = app.spawnModeId;
      const md = getSpawnMode(app.spawnModeId);
      modeSel.title = `${md.summary}\n\n${md.detail}`;
    }

    // n changes are reflected by the main subscribe → panel.syncFromApp path.
    // Tool chrome does not own control-panel state.

    if (toolStatus) {
      const flash = app.getToolFlashText();
      const sel = app.render.selectedIndex ?? -1;
      let text: string | null = flash;
      let state = app.toolFlash?.kind ?? "info";
      if (!text && app.pointerTool === "rewire" && sel >= 0) {
        text = `Source #${sel} → click target${app.playing ? "" : " · Space plays"}`;
        state = "info";
      } else if (!text && app.pointerTool === "inspect" && sel >= 0) {
        const f = app.sim.friends[sel]!;
        const e = app.sim.enemies[sel]!;
        text = `#${sel} · F#${f} · E#${e}${app.playing ? "" : " · Space plays"}`;
        state = "info";
      } else if (!text && app.pointerTool === "spawn") {
        const md = getSpawnMode(app.spawnModeId);
        text = app.playing
          ? `Spawn · ${md.label} · n=${app.sim.n} · ${md.hint}`
          : `Spawn · ${md.label} · n=${app.sim.n} · ${md.hint} · Space plays`;
        state = "friend";
      } else if (!text && app.pointerTool === "rewire") {
        text = "Rewire · click source, then target";
        state = "info";
      } else if (!text) {
        text = "Inspect · click a particle";
        state = "info";
      }
      toolStatus.hidden = false;
      toolStatus.textContent = text;
      toolStatus.dataset.state = state;
      toolStatus.title = text;
    }

    const canvasEl = document.getElementById("stage");
    const defCursor = POINTER_TOOLS.find((t) => t.id === app.pointerTool);
    if (canvasEl && defCursor) {
      canvasEl.style.cursor = defCursor.cursor;
    }
  };

  if (toolSel) {
    toolSel.value = app.pointerTool;
    toolSel.addEventListener("change", () => {
      app.setPointerTool(toolSel.value);
      sync();
    });
  }
  if (modeSel) {
    modeSel.value = app.spawnModeId;
    modeSel.addEventListener("change", () => {
      app.setSpawnMode(modeSel.value);
      sync();
    });
  }
  sync();
  return sync;
}
